import { Box, Tooltip, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    AutoSegmentationTaskState,
    DosePredictionTaskState,
    Job,
} from '~/state/atoms/jobAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';

interface JobDetailsProps {
    job: Job;
    anchorEl: HTMLDivElement | null;
}

export const JobDetails = ({ job, anchorEl }: JobDetailsProps) => {
    const renderJobDetails = () => {
        switch (job.type) {
            case WorkflowTypes.CONTOUR:
                const contouringTask = job.task as AutoSegmentationTaskState;
                return (
                    <Typography
                        variant="body2"
                        fontSize={12}
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {contouringTask?.model?.label} with{' '}
                        {contouringTask?.subSelectedStructures?.length}{' '}
                        structures
                    </Typography>
                );

            case WorkflowTypes.DOSE:
                const doseTask = job.task as DosePredictionTaskState;
                const targets = doseTask?.targets?.filter((t) => t.isTarget);
                return (
                    <Box display="flex" gap={0.5} alignItems="center">
                        <Tooltip
                            title={
                                <Box whiteSpace="nowrap">
                                    {targets?.map((t) => (
                                        <Box
                                            key={t.name}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Typography fontSize={12}>
                                                {t.name}
                                            </Typography>
                                            <Typography
                                                fontSize={12}
                                                color="primary"
                                            >
                                                {t.doseQuantity}
                                                Gy
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            }
                            placement="left-start"
                            PopperProps={{
                                anchorEl: anchorEl,
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontSize={12}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {doseTask?.model?.label} for {targets?.length}{' '}
                                {targets?.length === 1 ? 'target' : 'targets'}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            case WorkflowTypes.IMAGE:
            case WorkflowTypes.ADAPT:
            case WorkflowTypes.REGISTER_IMAGES:
                return (
                    <Typography
                        variant="body2"
                        fontSize={12}
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {job.task?.model?.label}
                    </Typography>
                );
            case WorkflowTypes.COMPOSITE_CBCT:
            case WorkflowTypes.DATA_UPLOAD:
                return null;
            default:
                return (
                    <Typography variant="body2" fontSize={12}>
                        Unknown task type
                    </Typography>
                );
        }
    };

    return (
        <>
            <Box display="flex" alignItems="center" gap={0.5}>
                <Typography
                    fontSize={14}
                    mr={1}
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {job.name}
                </Typography>
                {/* Surface the chip only for inputs unapproved at submit time. */}
                {job.inputApprovalStatus &&
                    job.inputApprovalStatus !==
                        DICOMApprovalStatus.APPROVED && (
                        <ReviewStatus
                            status={job.inputApprovalStatus}
                            fontSize={10}
                            tooltip={
                                <FormattedMessage id="jobInputApprovalTooltip" />
                            }
                        />
                    )}
            </Box>
            {renderJobDetails()}
        </>
    );
};
